import React, { useCallback } from "react";
import { Modal } from "@mui/material";
import {
  InfoOutlined as InfoIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { Trans, useTranslation } from "react-i18next";
import { DealerUsersProps } from "store/modules/DealerUsers/types.store";
import { handleDeleteDealerUserRequest } from "store/modules/DealerUsers/thunk.store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import * as Styled from "./styles";

import Button from "components/atoms/Button";
import { isDriverStoreType } from "types/coverageAreaType";
import { toast } from "utils/toast";

import { handleUpdateWorkingAreaStatusRequest } from "store/modules/Logistics/thunk.store";
import { AxiosError } from "axios";
import { CoverageAreaProps } from "store/modules/Logistics/types.store";

import { userProfile } from "enums/options/users";

interface ModalConfirmDeleteUserProps {
  user: DealerUsersProps | null;
  isOpen: boolean;
  onClose: () => void;
}

const ModalConfirmDeleteUser = ({
  isOpen,
  onClose,
  user,
}: ModalConfirmDeleteUserProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { workingAreas } = useAppSelector((state) => state.logistics);

  const typeProfile = user?.profile;

  const disableDeliveryManArea = useCallback(
    async (deliveryManArea: CoverageAreaProps): Promise<void> => {
      try {
        await dispatch(
          handleUpdateWorkingAreaStatusRequest({
            enabled: false,
            id: deliveryManArea.id,
            dealerId: user!.dealerId,
          }),
        ).unwrap();
      } catch (error: any) {
        if (error instanceof AxiosError) {
          toast(
            error.response?.data?.message || t("driverDeleteFail").toString(),
            {
              type: "error",
            },
          );
        } else {
          toast(t("driverDeleteFail").toString(), { type: "error" });
        }
      }
    },
    [dispatch, t, user],
  );

  const checkIfUserHasArea = useCallback(async (): Promise<void> => {
    const hasArea = workingAreas?.find(
      (area) =>
        isDriverStoreType(area.type) &&
        (area.drivers ?? []).some(
          (driver) => driver.details?.id === user?.id,
        ),
    );
    if (hasArea) {
      await disableDeliveryManArea(hasArea);
    }
  }, [disableDeliveryManArea, user?.id, workingAreas]);

  const handleDealerUserDelete = useCallback(async () => {
    try {
      await dispatch(
        handleDeleteDealerUserRequest({
          dealerId: user?.dealerId as string,
          userId: user?.id,
          profile: user?.profile,
        }),
      ).unwrap();

      await checkIfUserHasArea();

      if (typeProfile === userProfile.DELIVERYMAN) {
        toast(t("driverDeletedSuccessfully").toString(), {
          type: "success",
        });
        return;
      }

      toast(t("userDeletedSuccessfully").toString(), {
        type: "success",
      });
    } catch (err: any) {
      if (typeProfile === userProfile.DELIVERYMAN) {
        toast(err.errors[0].message.toString(), {
          type: "error",
          autoClose: false,
        });
      }
    } finally {
      onClose();
    }
  }, [
    checkIfUserHasArea,
    dispatch,
    onClose,
    t,
    typeProfile,
    user?.dealerId,
    user?.id,
    user?.profile,
  ]);

  return (
    <Modal open={isOpen} onClose={onClose} closeAfterTransition keepMounted>
      <Styled.Content>
        <Styled.ModalHeader>
          <Styled.Title>{t("delete")}</Styled.Title>
          <CloseIcon fontSize="large" color="inherit" onClick={onClose} />
        </Styled.ModalHeader>
        <Styled.Subtitle>
          {t("confirmDeleteUser", { user: user?.name })}
        </Styled.Subtitle>

        <Styled.DeleteUserInformationContainer>
          <InfoIcon fontSize="small" color="inherit" />
          <Styled.DeleteUserInformationText>
            <Trans
              i18nKey="deleteUserInformation"
              components={{ b: <strong /> }}
            />
          </Styled.DeleteUserInformationText>
        </Styled.DeleteUserInformationContainer>

        <Styled.ButtonsContainer>
          <Button onClick={onClose} variant="text">
            {t("deleteUserCancel")}
          </Button>
          <Button
            onClick={handleDealerUserDelete}
            variant="contained"
            color="error"
          >
            {t("deleteUserConfirm")}
          </Button>
        </Styled.ButtonsContainer>
      </Styled.Content>
    </Modal>
  );
};

export default ModalConfirmDeleteUser;
